import {
  dedupClustersResponseSchema,
  dedupSummarySchema,
  dismissDedupClustersResultSchema,
  exactDupApplyRequestSchema,
  exactDupGroupsResponseSchema,
  exactDupSummarySchema,
  jobSchema,
  type DedupApplyRequest,
  type DedupClustersResponse,
  type DedupRunRequest,
  type DedupSummary,
  type DismissDedupClustersResult,
  type ExactDupApplyRequest,
  type ExactDupGroupsResponse,
  type ExactDupSummary,
  type Job,
} from '@xphoto/shared';
import { ApiError } from '../gallery/api';
import { apiFetch } from '../lib/http';

/**
 * Client typé du DÉDOUBLONNAGE (T6b, XPHOTO-08/-09/-23). Consomme le backend T6a
 * déjà livré. Comme les autres clients, chaque réponse est RE-PARSÉE via son schéma
 * partagé `@xphoto/shared` (« never trust the client », y compris ses propres
 * réponses) : un backend divergent lève plutôt que de propager des données mal typées.
 *
 * Les erreurs sont propagées en `ApiError` (statut + code) — indispensable pour
 * DISCRIMINER le 409 `WRITE_DISABLED` (écriture désactivée sur la bibliothèque) du
 * chemin `apply`, et afficher un message dédié.
 */

async function readJson(res: Response): Promise<unknown> {
  if (!res.ok) {
    let code = 'UNKNOWN';
    try {
      const body = (await res.json()) as { error?: { code?: string } };
      if (typeof body?.error?.code === 'string') code = body.error.code;
    } catch {
      // Corps non-JSON : on garde le code générique.
    }
    throw new ApiError(res.status, code);
  }
  return res.json();
}

/**
 * `POST /api/libraries/:id/dedup` — enfile un job `dedup` (CLUSTERING). `threshold`
 * = distance de Hamming max pour lier deux `phash` (défaut serveur 8). Renvoie le
 * job créé (201) ; la progression se suit dans la cloche.
 */
export async function runDedup(libraryId: string, body: DedupRunRequest): Promise<Job> {
  const res = await apiFetch(`/api/libraries/${libraryId}/dedup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return jobSchema.parse(await readJson(res));
}

/** `GET /api/libraries/:id/dedup/clusters` — page de clusters + compteurs (pagination). */
export async function fetchClusters(
  libraryId: string,
  limit: number,
  offset: number,
  signal?: AbortSignal,
): Promise<DedupClustersResponse> {
  const sp = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const res = await apiFetch(`/api/libraries/${libraryId}/dedup/clusters?${sp.toString()}`, {
    signal: signal ?? null,
  });
  return dedupClustersResponseSchema.parse(await readJson(res));
}

/** `GET /api/libraries/:id/dedup/summary` — résumé (nb de clusters/doublons + dernière analyse). */
export async function fetchSummary(libraryId: string, signal?: AbortSignal): Promise<DedupSummary> {
  const res = await apiFetch(`/api/libraries/${libraryId}/dedup/summary`, {
    signal: signal ?? null,
  });
  return dedupSummarySchema.parse(await readJson(res));
}

/**
 * `POST /api/libraries/:id/dedup/apply` — enfile un job `dedup_apply` (SUPPRESSION
 * des fichiers `delete`). Renvoie le job créé (201). Une écriture désactivée renvoie
 * 409 `WRITE_DISABLED` → propagée en `ApiError` (statut 409, code `WRITE_DISABLED`).
 */
export async function applyDedup(libraryId: string, body: DedupApplyRequest): Promise<Job> {
  const res = await apiFetch(`/api/libraries/${libraryId}/dedup/apply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return jobSchema.parse(await readJson(res));
}

/**
 * `POST /api/libraries/:id/dedup/dismiss` — écarte des clusters « Images similaires »
 * déclarés NON doublons (faux positifs) : ils sont masqués de la revue et du résumé jusqu'à
 * la prochaine analyse. SYNCHRONE (écriture DB pure) → renvoie `{ dismissed }`, PAS un job.
 * Un 403 (bénéficiaire lecture seule) est propagé en `ApiError` (statut 403) → message dédié.
 */
export async function dismissClusters(
  libraryId: string,
  clusterIds: string[],
): Promise<DismissDedupClustersResult> {
  const res = await apiFetch(`/api/libraries/${libraryId}/dedup/dismiss`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clusterIds }),
  });
  return dismissDedupClustersResultSchema.parse(await readJson(res));
}

// ---------------------------------------------------------------------------
// Copies EXACTES (LOT 2/2 — branche le backend « copies exactes » 0.39.0)
// ---------------------------------------------------------------------------

/**
 * `GET /api/libraries/:id/dedup/exact` — page de GROUPES de copies exactes (≥2 chemins
 * physiques d'une même identité) + compteurs. L'inventaire vient du SCAN (pas d'analyse
 * à lancer ici). Réponse re-parsée via son schéma partagé.
 */
export async function fetchExactGroups(
  libraryId: string,
  limit: number,
  offset: number,
  signal?: AbortSignal,
): Promise<ExactDupGroupsResponse> {
  const sp = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const res = await apiFetch(`/api/libraries/${libraryId}/dedup/exact?${sp.toString()}`, {
    signal: signal ?? null,
  });
  return exactDupGroupsResponseSchema.parse(await readJson(res));
}

/**
 * `GET /api/libraries/:id/dedup/exact/summary` — résumé des copies exactes
 * (`groupCount`/`redundantCount`/`lastScanAt`). `lastScanAt` null = jamais scanné.
 */
export async function fetchExactSummary(
  libraryId: string,
  signal?: AbortSignal,
): Promise<ExactDupSummary> {
  const res = await apiFetch(`/api/libraries/${libraryId}/dedup/exact/summary`, {
    signal: signal ?? null,
  });
  return exactDupSummarySchema.parse(await readJson(res));
}

/**
 * `POST /api/libraries/:id/dedup/exact/apply` — enfile un job `dedup_exact_apply`
 * (SUPPRESSION des chemins redondants, conserve `keepRelPath` par groupe). Renvoie le
 * job créé (201). Écriture désactivée → 409 `WRITE_DISABLED` propagée en `ApiError`.
 *
 * Le corps est re-validé côté client (`exactDupApplyRequestSchema`) avant l'envoi —
 * les décisions sont dérivées d'un radiogroup, mais on ne fait pas confiance non plus
 * à nos propres entrées.
 */
export async function applyExact(libraryId: string, body: ExactDupApplyRequest): Promise<Job> {
  const parsed = exactDupApplyRequestSchema.parse(body);
  const res = await apiFetch(`/api/libraries/${libraryId}/dedup/exact/apply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(parsed),
  });
  return jobSchema.parse(await readJson(res));
}
